import {compact, find, forEach, get, isEmpty, isEqual, last, split, startsWith} from "lodash";
import {BEFORE_FIRST_DROP_ZONE_ID, BETWEEN_DROP_ZONE_PREFIX} from "../../Components/LearningPath/LearningPathLib";
import { useState } from 'react';
import { updateLearningPathOperation } from '../../Components/LearningPath/LearningPathLib';

/**
 * Custom hook for managing learning path graph state
 * @param {string} initialRootId - The root SignDoc ID for the learning path
 * @returns {Object} - Contains graph state and update function
 */
export const useGraphState = (initialRootId) => {
  const [graph, setGraph] = useState({
    rootId: initialRootId,
    edges: new Map(),
  });

  /**
   * Updates the graph structure based on the operation type
   * @param {Object} params - Parameters for the graph update
   * @param {string} params.operation - Type of operation (ADD, REMOVE, REORDER)
   * @param {string} params.parentId - Parent node ID
   * @param {string} params.documentId - The document being operated on
   * @param {string} params.sourceParentId - Original parent for REORDER operations
   * @returns {Object} - Updated graph structure
   */
  const updateGraphState = ({ operation, parentId, documentId, sourceParentId }) => {
    const newGraph = {
      ...graph,
      edges: new Map(graph.edges)
    };

    switch (operation) {
      case updateLearningPathOperation.ADD: {
        // Store existing child to maintain chain
        const existingChildId = graph.edges.get(parentId);
        
        // Set new parent-child relationship
        newGraph.edges.set(parentId, documentId);
        
        // If the parent already had a child, make it a child of the newly added node
        if (existingChildId) {
          newGraph.edges.set(documentId, existingChildId);
        }
        break;
      }
      
      case updateLearningPathOperation.REMOVE: {
        // Get child of the node to be removed
        let existingChildId = graph.edges.get(documentId);
        
        if (parentId && existingChildId) {
          // Connect the parent directly to the child of the removed node
          newGraph.edges.set(parentId, existingChildId);
        } else if (parentId) {
          // If no child exists, simply remove the parent's connection
          newGraph.edges.delete(parentId);
        }
        
        // Remove the document's own entry
        newGraph.edges.delete(documentId);
        break;
      }
      
      case updateLearningPathOperation.REORDER: {
        // No change needed if source and target are the same
        if (sourceParentId === parentId) {
          break;
        }
        
        // Get descendants of both affected nodes
        const movedDocChildId = graph.edges.get(documentId);
        const targetParentChildId = graph.edges.get(parentId);
        
        // Set the document as a child of its new parent
        newGraph.edges.set(parentId, documentId);
        
        // Reconnect the source parent to the child of the moved document
        if (movedDocChildId) {
          newGraph.edges.set(sourceParentId, movedDocChildId);
        } else {
          // If the moved document had no children, remove the connection
          newGraph.edges.delete(sourceParentId);
        }
        
        // Make the moved document point to the previous child of its new parent
        if (targetParentChildId) {
          // Safety check to prevent circular references which can cause browser crashes
          if (targetParentChildId !== documentId) {
            newGraph.edges.set(documentId, targetParentChildId);
          } else {
            console.warn("Prevented circular reference in graph");
          }
        } else {
          // If moving to the end of the chain, ensure we don't have an existing reference
          newGraph.edges.delete(documentId);
        }
        break;
      }
      
      default:
        console.error(`Unknown operation: ${operation}`);
        return graph; // Return unchanged graph on error
    }

    // Update state with new graph
    setGraph(newGraph);
    return newGraph;
  };

  return { 
    graph, 
    updateGraphState,
    setGraph  // Expose setGraph for direct updates when needed
  };
};

/**
 * Gets documents in the learning path in correct sequence order
 * @param {Object} params - Parameters
 * @param {Object} params.graph - Graph structure with rootId and edges
 * @param {Array} params.allDocuments - Full list of documents to match IDs against
 * @returns {Array} - Ordered list of document objects
 */
export const getSequenceOrderedDocuments = ({graph, allDocuments}) => {
    const rootId = get(graph, 'rootId');
    if (isEmpty(rootId)) return [];

    const edges = get(graph, 'edges', new Map());
    const ordered = [];
    let currentId = rootId;

    // Linear traversal through the chain
    while (edges.has(currentId)) {
        currentId = edges.get(currentId);
        ordered.push(find(allDocuments, { id: currentId }));
    }

    // Ensure no undefined/null values in result
    return compact(ordered);
};

/**
 * Calculates target document IDs for insertion based on drop zone ID
 * @param {Object} params - Parameters
 * @param {string} params.overId - The ID of the drop target
 * @param {Object} params.graph - The current graph structure
 * @param {Array} params.allDocuments - List of all documents
 * @param {boolean} params.isReordering - Indicates if the operation is a reordering
 * @param {string} params.landOnDocId - The ID of the document being landed on
 * @param {string} params.actingId - The ID of the acting document
 * @returns {Object} - Contains parentId and position information
 */
export const calculateNewParent = ({overId, graph, allDocuments, isReordering, landOnDocId, actingId}) => {
    // Drop at the end of the sequence
    if (isEqual(overId, 'drop-zone')) {
        const orderedDocs = getSequenceOrderedDocuments({graph, allDocuments});
        
        // If we're reordering and the element is already the last element in the sequence
        // then we return null to indicate no change should be made
        if (isReordering && !isEmpty(orderedDocs) && get(last(orderedDocs), 'id') === actingId) {
            console.log('Element is already at the end, no change needed');
            return null;
        }
        
        return isEmpty(orderedDocs) ?
            get(graph, 'rootId') :
            get(last(orderedDocs), 'id');
    }
    // Drop before the first item
    else if (isEqual(overId, `drop-zone-${BEFORE_FIRST_DROP_ZONE_ID}`)) {
        return get(graph, 'rootId');
    }
    // Drop between two documents
    else if (startsWith(overId, `drop-zone-${BETWEEN_DROP_ZONE_PREFIX}`)) {

        const betweenSection = get(split(overId, '-between-'), 1, '');
        const parentAndChildIds = split(betweenSection, ':');
        const parentId = get(parentAndChildIds, 0);
        const childId = get(parentAndChildIds, 1);
        if (isReordering) {
            // Case 1: Document is being dropped on its own drop zone
            if (landOnDocId === actingId) {
                return null
            }
            // Case 2: Moving UP in the hierarchy
            // If we're dropping between parent X and child Y, and Y is currently our parent,
            // then we're moving up
            if (actingId === childId && landOnDocId === parentId) {
                return findParentInGraph({documentId: parentId, graph});
            }
            // Case 3: Moving DOWN in the hierarchy
            // If we're dropping between parent X and child Y, and X is us and we land on Y,
            // then we're moving down
            if (actingId === parentId && landOnDocId === childId) {
                return childId;
            }
        }
        // Case 4: Non-adjacent move
        // If we're dropping between parent X and child Y,
        // and neither is directly related to us
        // Default case for non-reordering operations
        return parentId;
    }
    // Error case
    else {
        console.log('Error: Dropped on an unexpected element ID:', overId);
        return null;
    }
};

/**
 * Finds the parent node ID of a document in the graph
 * @param {Object} params - Parameters
 * @param {string} params.documentId - ID of the document to find parent for
 * @param {Object} params.graph - The graph data structure
 * @returns {string} - ID of the parent document
 */
export const findParentInGraph = ({documentId, graph}) => {
    // Iterate Map entries directly instead of converting to object
    let parentId = null;
    for (const [key, value] of graph.edges.entries()) {
        if (value === documentId) {
            parentId = key;
            break;
        }
    }
    // Return the found parentId or rootId if no parent exists (document might be the first in the chain)
    return parentId || graph.rootId;
};